import { GetPatientsBalanceIO } from '../types';
import { useCommands1Queries } from './use-commands1-queries';
import { UseCommands1QueryEndpointArgs, useCommands1Query } from './use-commands1-query';

/**
 * A hook that returns a query for the `GetPatientsBalance` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetPatientsBalanceQuery = <E = unknown, D = GetPatientsBalanceIO['output']>(
  args: UseCommands1QueryEndpointArgs<'GetPatientsBalance', E, D>
) =>
  useCommands1Query({
    endpointName: 'GetPatientsBalance',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetPatientsBalance` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetPatientsBalanceQueries = <E = unknown, D = GetPatientsBalanceIO['output']>(
  argsArr: UseCommands1QueryEndpointArgs<'GetPatientsBalance', E, D>[]
) =>
  useCommands1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetPatientsBalance',
      ...args,
    }))
  );
